import type { AddendumChange } from '@/lib/schemas/db'

/**
 * The service fields a revision tracks, with the labels shown in the change
 * history — the service twin of `customerRevisionFields.ts`, one list across
 * callouts, sessions and jobs (a field absent from a service type can never
 * differ, so it never produces a change).
 *
 * Against PocketBase this list is display vocabulary only — the
 * service-revisions hook diffs every column server-side and keeps its own
 * label map (pb_hooks/lib/service-revisions.js; keep the two in sync). In the
 * in-memory adapter this list drives the demo-mode trail.
 *
 * Deliberately excluded: `status` (status changes keep their own trail),
 * `devicePassword` (encrypted secret — must never land in a readable
 * revision), and the system/identity fields.
 */
export const SERVICE_REVISION_FIELDS = [
  { field: 'issueSummary', label: 'Issue' },
  { field: 'startTime', label: 'Start time' },
  { field: 'endTime', label: 'End time' },
  { field: 'isWholeDay', label: 'Whole day' },
  { field: 'customerId', label: 'Customer' },
  { field: 'contactAddress', label: 'Visit address' },
  { field: 'contactName', label: 'Site contact' },
  { field: 'contactPhone', label: 'Phone' },
  { field: 'contactEmail', label: 'Email' },
  { field: 'invoiceName', label: 'Invoice to' },
  { field: 'invoiceAddress', label: 'Invoice address' },
  { field: 'invoiceVat', label: 'VAT number' },
  { field: 'invoiceEmail', label: 'Invoice email' },
  { field: 'invoicePhone', label: 'Invoice phone' },
  { field: 'invoiceId', label: 'Invoice number (Omni)' },
  { field: 'billablesConsumed', label: 'Billables' },
  { field: 'fault', label: 'Fault' },
  { field: 'deviceType', label: 'Device type' },
  { field: 'deviceDescription', label: 'Device(s) description' },
  { field: 'peripherals', label: 'Peripherals' },
  { field: 'numberTag', label: 'Number tag' },
  { field: 'billingOutcome', label: 'Billing outcome' },
  { field: 'readyForCollectionAt', label: 'Ready for collection at' },
  { field: 'collectedAt', label: 'Collected at' },
  { field: 'checkedInSignedAt', label: 'Checked in (signed) at' },
  { field: 'checkedOutSignedAt', label: 'Checked out (signed) at' },
  { field: 'checkedOutName', label: 'Collected by' },
  { field: 'checkedOutPhone', label: 'Collection phone' },
  { field: 'checkedInBy', label: 'Checked in by' },
  { field: 'checkedOutBy', label: 'Checked out by' },
  { field: 'checkedInNoSignatureReason', label: 'No check-in signature reason' },
  { field: 'checkedOutNoSignatureReason', label: 'No check-out signature reason' },
] as const

export type ServiceRevisionFieldKey =
  (typeof SERVICE_REVISION_FIELDS)[number]['field']

export function serviceRevisionFieldLabel(field: string): string {
  return (
    SERVICE_REVISION_FIELDS.find((entry) => entry.field === field)?.label ??
    field
  )
}

/**
 * Values must compare (and display) as stable strings: JSON values get
 * key-sorted stringification so key order alone never reads as a change —
 * matching the server hook's normalization exactly.
 */
export function normalizeRevisionValue(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (Array.isArray(value) || typeof value === 'object') {
    const json = JSON.stringify(sortKeys(value))
    // Unset and empty mean the same thing to the reader.
    return json === '[]' || json === '{}' ? '' : json
  }
  const text = String(value)
  // PB stores datetimes with a space separator, the app sends 'T' — the same
  // instant must never read as a change.
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(text)) {
    return text.replace(' ', 'T')
  }
  return text
}

function sortKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map((item) => sortKeys(item))
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const key of Object.keys(value as Record<string, unknown>).sort()) {
      out[key] = sortKeys((value as Record<string, unknown>)[key])
    }
    return out
  }
  return value
}

/**
 * Diff two full service snapshots (before and after an update), dropping
 * everything that did not actually change. Used by the in-memory adapter to
 * mirror what the server hook records in PocketBase mode.
 */
export function buildServiceRevisionChanges(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
): AddendumChange[] {
  return SERVICE_REVISION_FIELDS.flatMap((entry) => {
    const previous = normalizeRevisionValue(before[entry.field])
    const next = normalizeRevisionValue(after[entry.field])
    if (previous === next) return []
    return [{ field: entry.field, label: entry.label, before: previous, after: next }]
  })
}
